"use client";

import clsx from "clsx";
import { DawnDuskMidnightTheme, Form } from "../../../sanity/types";
import styles from "./FormBlock.module.scss";
import { InputField } from "../../form/fields/InputField/InputField";
import { FormProvider, useForm } from "react-hook-form";
import { Fragment, useMemo, useRef, useState } from "react";
import { acceptedFiles, SEND_PROPERTIES, siteAPIURL } from "../../../utilities/constants";
import { Fieldset } from "../../form/Fieldset/Fieldset";
import { RadioGroupField } from "../../form/fields/RadioGroupField/RadioGroupField";
import { CheckboxGroupField } from "../../form/fields/CheckboxGroupField/CheckboxGroupField";
import { Button } from "../../shared/Button/Button";
import { DropdownField } from "../../form/fields/DropdownField/DropdownField";
import { FileUploadField } from "../../form/fields/FileUploadField/FileUploadField";

const { SEND_FROM, SEND_TO, SEND_TIME, SEND_SUBJECT } = SEND_PROPERTIES;

interface FormBlockProps {
	form?: Form | null;
	theme?: DawnDuskMidnightTheme | null;
}

const mapKeysToLabels = (data?: FormBlockProps["form"]): { [key: string]: string } => {
	if (!data?.fieldsets) return {};
	return data.fieldsets.reduce(
		(outerMap, fieldset) => {
			if (!fieldset.fields) return {};
			return fieldset.fields.reduce((innerMap, field) => {
				const fullLabel = `${fieldset.heading}: ${field.label}`;
				innerMap[`${fieldset._key}:${field._key}`] = fullLabel;
				return innerMap;
			}, outerMap);
		},
		{} as { [key: string]: string },
	);
};

const requiredMessage = (isMandatory?: boolean) => (isMandatory ? "This field is required" : false);

export const FormBlock = ({ form, theme }: FormBlockProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [sent, setSent] = useState<boolean>(false);

	const formMethods = useForm();
	const {
		control,
		handleSubmit,
		formState: { isSubmitting },
	} = formMethods;

	const keyLabelMap = useMemo(() => mapKeysToLabels(form), [form]);

	if (!form) return null;

	const onSubmit = async (data: Record<string, any>) => {
		const mappedData: {
			[key: string]: {
				label: string;
				value: string;
			};
		} = {};

		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				for (const labelKey in keyLabelMap) {
					if (key.startsWith(labelKey)) {
						const baseKey = labelKey;
						const label = keyLabelMap[baseKey];
						const value = data[key];

						// Create a unique identifier if it is a checkbox item
						const checkboxItem = key.substring(baseKey.length + 1);

						// Create a unique key for the mappedData object
						const mappedKey = checkboxItem.length > 0 ? `${baseKey} ${checkboxItem}` : baseKey;

						// Create a unique label for the mappedData object
						const uniqueLabel = checkboxItem.length > 0 ? `${label}: ${checkboxItem}` : label;

						// Add the entry to the mappedData object
						mappedData[mappedKey] = {
							label: uniqueLabel,
							value: checkboxItem.length > 0 ? value : data[key],
						};
					}
				}
			}
		}

		// Unmapped data to it's original form
		const formData = new FormData();
		formData.append(SEND_FROM, form.sendingEmail || "");
		formData.append(SEND_TO, form.targetEmail || "");
		formData.append(
			SEND_TIME,
			new Date().toLocaleString("en-US", { timeZone: "Australia/Sydney" }),
		);
		formData.append(SEND_SUBJECT, form.title || "Form submission");

		Object.values(mappedData).map(inputData => {
			if (
				Array.isArray(inputData.value) &&
				inputData.value.every((files: Blob) => acceptedFiles.includes(files.type))
			) {
				inputData.value.map((file: Blob) => {
					debugger;
					formData.append("attachments", file);
				});
				return;
			}
			formData.append(inputData.label, inputData.value);
		});

		const result = await postForm(siteAPIURL + "/email", formData);
		if (!result.success) {
			alert(result.message);
			return;
		} else {
			setSent(true);
			wrapperRef.current?.scrollIntoView({ behavior: "smooth" });
		}
	};

	return (
		<section
			className={clsx(styles["form-block"], styles[`form-block--${theme}`])}
			ref={wrapperRef}
		>
			<div className={clsx(styles["form-block__wrapper"], "grid-cols-12")}>
				{sent && (
					<div className={clsx(styles["form-block__success"], "col-full")}>
						<h2>Thank you</h2>
						<p>One of our team will be in touch as soon as possible.</p>
					</div>
				)}
				{!sent && (
					<div className={clsx(styles["form-block__inner"], "col-full col-md-8 col-lg-5")}>
						<FormProvider {...formMethods}>
							<form
								onSubmit={handleSubmit(onSubmit)}
								className={clsx(styles["form-block__form"], "col-full")}
							>
								{form?.fieldsets?.map((fieldset, index) => (
									<div key={fieldset._key}>
										<Fieldset
											className={styles["form-block__fieldset"]}
											legend={fieldset.heading || ""}
										>
											{fieldset.fields?.map(field => {
												const name = `${fieldset._key}:${field._key}`;
												switch (field.fieldType) {
													case "text":
														return (
															<InputField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																}}
																placeholder={field.placeholderText}
																hideLabel={!field.showLabelText}
															/>
														);
													case "date":
														return (
															<InputField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																	pattern: {
																		value: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
																		message: "Please enter a valid date",
																	},
																}}
																placeholder={"DD/MM/YYYY"}
																hideLabel={!field.showLabelText}
															/>
														);
													case "email":
														return (
															<InputField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																	pattern: {
																		value: /\S+@\S+\.\S+/,
																		message: "Please enter a valid email address",
																	},
																}}
																placeholder={"example@domain.com"}
																hideLabel={!field.showLabelText}
															/>
														);
													case "phone":
														return (
															<InputField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																	pattern: {
																		value:
																			/^((\+61\s?)?(\((0|02|03|04|07|08)\))?)?\s?\d{1,4}\s?\d{1,4}\s?\d{0,4}$/,
																		message: "Please enter a valid phone number",
																	},
																}}
																placeholder={"+61"}
																hideLabel={!field.showLabelText}
															/>
														);
													case "number":
														return (
															<InputField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																	pattern: { value: /\d/, message: "Please enter a number" },
																}}
																placeholder={field.placeholderText}
																hideLabel={!field.showLabelText}
															/>
														);
													case "radio":
														return (
															<RadioGroupField
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																}}
																options={field.radioItems?.map(item => ({
																	label: item,
																	value: item,
																}))}
																hideLabel={!field.showLabelText}
															/>
														);
													case "checkbox":
														return (
															<CheckboxGroupField
																key={field._key}
																label={field.label || ""}
																name={name}
																help={
																	field.isMandatory
																		? field.checkboxValidationStrategy === "all"
																			? "Please tick all checkboxes to proceed"
																			: "Please select at least one"
																		: undefined
																}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																	validate: {
																		oneOrMany: value => {
																			const isMandatory = field.isMandatory;
																			const strategy = field.checkboxValidationStrategy || "one";
																			if (isMandatory) {
																				switch (strategy) {
																					case "one":
																						return value.length > 0 || "Please select at least one";
																					case "all":
																						return (
																							value.length === (field.checkboxItems?.length || 0) ||
																							"Please tick all checkboxes to proceed"
																						);
																				}
																			}
																			return true;
																		},
																	},
																}}
																options={field.checkboxItems?.map(item => ({
																	label: item,
																	value: item,
																}))}
																hideLabel={!field.showLabelText}
															/>
														);
													case "dropdown":
														return (
															<DropdownField
																control={control}
																key={field._key}
																label={field.label || ""}
																placeholder={field.placeholderText}
																name={name}
																registerOptions={{
																	required: requiredMessage(field.isMandatory),
																}}
																options={field.dropdownItems?.map(item => ({
																	label: item,
																	value: item,
																}))}
																hideLabel={!field.showLabelText}
															/>
														);

													case "file":
														return (
															<FileUploadField
																control={control}
																key={field._key}
																label={field.label || ""}
																name={name}
																registerOptions={{ required: requiredMessage(field.isMandatory) }}
																hideLabel={!field.showLabelText}
															/>
														);
													default:
														return null;
												}
											})}
										</Fieldset>
									</div>
								))}

								<Button
									className={styles["form-block__submit"]}
									type="submit"
									disabled={isSubmitting}
									label={isSubmitting ? `Submitting ...` : form?.submitButtonText}
								/>
							</form>
						</FormProvider>
					</div>
				)}
			</div>
		</section>
	);
};

const postForm = async (url: string, data: unknown) => {
	try {
		const response = await fetch(url, {
			method: "POST",
			body: data as BodyInit,
		});
		if (!response.ok) {
			const res = await response.json();
			throw new Error(res.message);
		}
		return { success: true };
	} catch (error) {
		if (error instanceof Error) return { success: false, message: error.message };
		return {
			success: false,
			message: "Mail not sent due to Unknown error, please contact site admin",
		};
	}
};
